import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import moment from 'moment-timezone';

const ARGENTINA_TIMEZONE = 'America/Argentina/Buenos_Aires';
const SESSION_HISTORY_DAYS = 90;

const PERIOD_OPTIONS = [
    { value: '1', label: 'Últimos 30 dias' },
    { value: '3', label: 'Últimos 3 meses' },
];

const SERVICE_CONFIG = {
    1: { name: 'Sala Libre', color: '#10b981', bgColor: '#dcfce7', icon: '🏃' },
    2: { name: 'HIFT', color: '#f59e0b', bgColor: '#fef3c7', icon: '🔥' },
    3: { name: 'Personalizado', color: '#ef4444', bgColor: '#fee2e2', icon: '⭐' }
};

const MemberAttendanceTracker = () => {
    const navigate = useNavigate();
    const [state, setState] = useState({
        users: [],
        sessions: [],
        userSearchText: '',
        selectedUserId: '',
        selectedUserName: '',
        selectedPeriod: '1',
        filteredUsers: [],
        showUserDropdown: false,
        loadingUsers: true,
        loadingSessions: false,
        error: ''
    });

    useEffect(() => {
        let isMounted = true;

        fetch('https://iron.nextriking.com/api/users')
            .then(response => response.ok ? response.json() : Promise.reject())
            .then(data => {
                if (isMounted && data.body) {
                    setState(prev => ({ ...prev, users: data.body, loadingUsers: false }));
                }
            })
            .catch(error => {
                console.error('Error al cargar usuarios:', error);
                if (isMounted) {
                    setState(prev => ({
                        ...prev,
                        loadingUsers: false,
                        error: 'Error al cargar usuarios'
                    }));
                }
            });

        return () => { isMounted = false; };
    }, []);

    useEffect(() => {
        if (!state.userSearchText.trim() || state.userSearchText.length < 2) {
            setState(prev => ({ ...prev, filteredUsers: [], showUserDropdown: false }));
            return;
        }

        const filtered = state.users.filter(user => {
            const fullName = `${user.name} ${user.lastname}`.toLowerCase();
            const dni = user.documentNumber?.toString() || '';
            const searchLower = state.userSearchText.toLowerCase();
            return fullName.includes(searchLower) || dni.includes(searchLower);
        }).slice(0, 5);

        setState(prev => ({
            ...prev,
            filteredUsers: filtered,
            showUserDropdown: filtered.length > 0
        }));
    }, [state.userSearchText, state.users]);
    const loadUserSessions = useCallback(async (userId) => {
        setState(prev => ({ ...prev, loadingSessions: true, error: '' }));

        try {
            const response = await fetch(
                `https://iron.nextriking.com/api/sessions/user/${userId}?days=${SESSION_HISTORY_DAYS}`
            );
            if (!response.ok) throw new Error('Error al cargar sesiones');

            const data = await response.json();

            if (!Array.isArray(data.body)) {
                throw new Error('Los datos de sesiones no están en formato de array');
            }

            const userSessions = data.body.map(session => ({
                ...session,
                loginTimestamp: new Date(session.login).getTime()
            }));

            setState(prev => ({
                ...prev,
                sessions: userSessions,
                loadingSessions: false
            }));
        } catch (error) {
            console.error('Error loading sessions:', error);
            setState(prev => ({
                ...prev,
                loadingSessions: false,
                error: `Error al cargar las sesiones: ${error.message}`,
                sessions: []
            }));
        }
    }, []);

    const handleUserSearch = useCallback((e) => {
        const value = e.target.value;
        setState(prev => ({
            ...prev,
            userSearchText: value,
            selectedUserId: '',
            selectedUserName: '',
            sessions: []
        }));
    }, []);

    const handleUserSelect = useCallback((user) => {
        setState(prev => ({
            ...prev,
            selectedUserId: user.id,
            selectedUserName: `${user.name} ${user.lastname}`,
            userSearchText: `${user.name} ${user.lastname}`,
            showUserDropdown: false
        }));
        loadUserSessions(user.id);
    }, [loadUserSessions]);

    const handleInputBlur = useCallback(() => {
        setTimeout(() => {
            setState(prev => ({ ...prev, showUserDropdown: false }));
        }, 150);
    }, []);

    const handleInputFocus = useCallback(() => {
        if (state.userSearchText && state.filteredUsers.length > 0) {
            setState(prev => ({ ...prev, showUserDropdown: true }));
        }
    }, [state.userSearchText, state.filteredUsers.length]);

    const handleClear = useCallback(() => {
        setState(prev => ({
            ...prev,
            userSearchText: '',
            selectedUserId: '',
            selectedUserName: '',
            sessions: [],
            error: ''
        }));
    }, []);

    const handleBack = useCallback(() => {
        if (window.history.length > 1) {
            navigate(-1);
            return;
        }

        navigate('/dashboard');
    }, [navigate]);

    const { filteredSessions } = useMemo(() => {
        if (!state.sessions.length) {
            return {
                filteredSessions: []
            };
        }

        const monthsBack = parseInt(state.selectedPeriod);
        const now = moment().tz(ARGENTINA_TIMEZONE);
        const startTimestamp = now.clone().subtract(monthsBack, 'months').valueOf();
        const endTimestamp = now.valueOf();

        const filtered = state.sessions.filter(session => {
            return session.loginTimestamp >= startTimestamp && session.loginTimestamp <= endTimestamp;
        });

        return { filteredSessions: filtered };
    }, [state.sessions, state.selectedPeriod]);

    const monthlyStats = useMemo(() => {
        if (!filteredSessions.length) return [];

        // Agrupar sesiones por mes/año
        const monthGroups = {};

        filteredSessions.forEach(session => {
            const sessionDate = moment(session.login).tz(ARGENTINA_TIMEZONE);
            const monthKey = sessionDate.format('YYYY-MM');
            const monthName = sessionDate.locale('es').format('MMMM [de] YYYY');

            if (!monthGroups[monthKey]) {
                monthGroups[monthKey] = {
                    name: monthName,
                    count: 0,
                    date: sessionDate.valueOf()
                };
            }
            monthGroups[monthKey].count++;
        });

        // Convertir a array y ordenar por fecha
        return Object.values(monthGroups)
            .sort((a, b) => a.date - b.date)
            .map(group => ({
                name: group.name,
                count: group.count
            }));
    }, [filteredSessions]);


    return (
        <div style={{
            minHeight: '100vh',
            backgroundColor: '#f5f7fa',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
        }}>
            <div style={{
                background: 'linear-gradient(135deg, #4a6eb5 0%, #5b7db1 100%)',
                color: 'white',
                padding: '20px 40px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                    <div style={{
                        backgroundColor: 'white',
                        borderRadius: '8px',
                        width: '40px',
                        height: '40px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '20px'
                    }}>
                        📊
                    </div>
                    <h1 style={{ margin: 0, fontSize: '24px', fontWeight: '600' }}>
                        Consulta de Asistencias
                    </h1>
                </div>
                <button
                    onClick={handleBack}
                    style={{
                        backgroundColor: 'rgba(255,255,255,0.2)',
                        border: '1px solid rgba(255,255,255,0.3)',
                        color: 'white',
                        padding: '8px 16px',
                        borderRadius: '6px',
                        fontSize: '14px',
                        cursor: 'pointer'
                    }}
                >
                        ← Volver atrás
                </button>
            </div>

            <div style={{ padding: '30px 40px', maxWidth: '1400px', margin: '0 auto' }}>



                <div style={{
                    backgroundColor: 'white',
                    borderRadius: '12px',
                    padding: '0',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                    border: '1px solid #e5e9f2',
                    marginBottom: '20px'
                }}>
                    <div style={{
                        padding: '20px 30px',
                        borderBottom: '1px solid #e5e9f2',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px'
                    }}>
                        <div style={{
                            backgroundColor: '#f8f9fc',
                            borderRadius: '8px',
                            width: '32px',
                            height: '32px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '16px'
                        }}>
                            🔍
                        </div>
                        <h2 style={{
                            margin: 0,
                            fontSize: '18px',
                            fontWeight: '600',
                            color: '#2d3748'
                        }}>
                            Búsqueda de Usuario
                        </h2>
                    </div>

                    <div style={{ padding: '30px' }}>
                        <div style={{ marginBottom: '20px' }}>
                            <label style={{
                                display: 'block',
                                marginBottom: '12px',
                                fontSize: '14px',
                                fontWeight: '600',
                                color: '#4a5568'
                            }}>
                                Buscar por nombre o DNI
                            </label>

                            {state.loadingUsers ? (
                                <div style={{
                                    padding: '12px',
                                    border: '1px solid #e2e8f0',
                                    borderRadius: '8px',
                                    backgroundColor: '#f7fafc',
                                    color: '#718096',
                                    fontSize: '14px'
                                }}>
                                    Cargando usuarios...
                                </div>
                            ) : (
                                <div style={{ position: 'relative', width: '100%' }}>
                                    <input
                                        type="text"
                                        value={state.userSearchText}
                                        onChange={handleUserSearch}
                                        onFocus={handleInputFocus}
                                        onBlur={handleInputBlur}
                                        placeholder="Escriba el nombre del usuario o DNI..."
                                        style={{
                                            width: '100%',
                                            padding: '12px 16px',
                                            border: '1px solid #e2e8f0',
                                            borderRadius: state.showUserDropdown ? '8px 8px 0 0' : '8px',
                                            fontSize: '14px',
                                            backgroundColor: 'white',
                                            color: '#2d3748',
                                            outline: 'none',
                                            boxSizing: 'border-box'
                                        }}
                                    />

                                    {state.showUserDropdown && (
                                         <div style={{
                                             position: 'absolute',
                                             top: '100%',
                                            left: 0,
                                            right: 0,
                                            backgroundColor: 'white',
                                            border: '1px solid #e2e8f0',
                                            borderTop: 'none',
                                            borderRadius: '0 0 8px 8px',
                                            boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                                            zIndex: 1000,
                                             maxHeight: '200px',
                                             overflowY: 'auto'
                                         }}>
                                            {state.filteredUsers.map((user, index) => (
                                                <div
                                                    key={user.id}
                                                    style={{
                                                        padding: '12px 16px',
                                                        cursor: 'pointer',
                                                        borderBottom: index === state.filteredUsers.length - 1 ? 'none' : '1px solid #f7fafc'
                                                    }}
                                                    onMouseEnter={(e) => e.target.style.backgroundColor = '#f7fafc'}
                                                    onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                                                    onClick={() => handleUserSelect(user)}
                                                >
                                                    <div style={{ fontWeight: '500', color: '#2d3748' }}>
                                                        {user.name} {user.lastname}
                                                    </div>
                                                    <div style={{ fontSize: '12px', color: '#718096', marginTop: '2px' }}>
                                                        DNI: {user.documentNumber}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {state.userSearchText && state.filteredUsers.length === 0 && !state.loadingUsers && (
                                        <div style={{
                                            position: 'absolute',
                                            top: '100%',
                                            left: 0,
                                            right: 0,
                                            backgroundColor: 'white',
                                            border: '1px solid #e2e8f0',
                                            borderTop: 'none',
                                            borderRadius: '0 0 8px 8px',
                                            boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                                            zIndex: 1000,
                                            padding: '12px 16px',
                                            color: '#718096',
                                            fontSize: '14px',
                                            textAlign: 'center'
                                        }}>
                                            No se encontraron usuarios
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {state.selectedUserId && (
                            <div style={{
                                backgroundColor: '#eff6ff',
                                color: '#1e40af',
                                padding: '12px 16px',
                                borderRadius: '8px',
                                marginBottom: '20px',
                                border: '1px solid #93c5fd',
                                fontSize: '14px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between'
                            }}>
                                <div>
                                    <span style={{ fontWeight: '500' }}>
                                        Usuario seleccionado: {state.selectedUserName}
                                    </span>
                                </div>
                                <button
                                    onClick={handleClear}
                                    style={{
                                        backgroundColor: 'transparent',
                                        border: 'none',
                                        color: '#1e40af',
                                        cursor: 'pointer',
                                        fontSize: '14px',
                                        textDecoration: 'underline'
                                    }}
                                >
                                    Cambiar usuario
                                </button>
                            </div>
                        )}

                        {state.error && (
                            <div style={{
                                backgroundColor: '#fed7d7',
                                color: '#742a2a',
                                padding: '12px 16px',
                                borderRadius: '8px',
                                marginBottom: '20px',
                                border: '1px solid #feb2b2',
                                fontSize: '14px'
                            }}>
                                {state.error}
                            </div>
                        )}

                        {state.loadingSessions && (
                            <div style={{
                                backgroundColor: '#f0f9ff',
                                color: '#0369a1',
                                padding: '12px 16px',
                                borderRadius: '8px',
                                marginBottom: '20px',
                                border: '1px solid #7dd3fc',
                                fontSize: '14px'
                            }}>
                                Cargando sesiones...
                            </div>
                        )}
                    </div>
                </div>

                {state.selectedUserId && (
                    <div style={{
                        backgroundColor: 'white',
                        borderRadius: '12px',
                        padding: '0',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                        border: '1px solid #e5e9f2'
                    }}>
                        <div style={{
                            padding: '20px 30px',
                            borderBottom: '1px solid #e5e9f2',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: '16px',
                            flexWrap: 'wrap'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <div style={{
                                    backgroundColor: '#f8f9fc',
                                    borderRadius: '8px',
                                    width: '32px',
                                    height: '32px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '16px'
                                }}>
                                    📋
                                </div>
                                <h2 style={{
                                    margin: 0,
                                    fontSize: '18px',
                                    fontWeight: '600',
                                    color: '#2d3748'
                                }}>
                                    Historial de Sesiones
                                </h2>
                            </div>

                            {/* NUEVA SECCIÓN: Estadísticas por mes */}
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '20px',
                                flexWrap: 'wrap'
                            }}>
                                {PERIOD_OPTIONS.map(option => (
                                    <button
                                        key={option.value}
                                        onClick={() => setState(prev => ({ ...prev, selectedPeriod: option.value }))}
                                        style={{
                                            padding: '8px 16px',
                                            borderRadius: '6px',
                                            fontSize: '14px',
                                            fontWeight: '500',
                                            cursor: 'pointer',
                                            border: state.selectedPeriod === option.value ? 'none' : '1px solid #e2e8f0',
                                            backgroundColor: state.selectedPeriod === option.value ? '#4a6eb5' : '#f8f9fc',
                                            color: state.selectedPeriod === option.value ? 'white' : '#4a5568',
                                            transition: 'all 0.2s'
                                        }}
                                    >
                                        {option.label}
                                    </button>
                                ))}
                                {monthlyStats.map((month, index) => (
                                    <div key={index} style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                        padding: '6px 12px',
                                        backgroundColor: '#f8f9fc',
                                        borderRadius: '6px',
                                        border: '1px solid #e2e8f0'
                                    }}>
                                        <span style={{
                                            fontSize: '13px',
                                            color: '#4a5568',
                                            fontWeight: '500'
                                        }}>
                                            {month.name.charAt(0).toUpperCase() + month.name.slice(1)}
                                        </span>
                                        <span style={{
                                            fontSize: '14px',
                                            fontWeight: '600',
                                            backgroundColor: '#4a6eb5',
                                            color: 'white',
                                            padding: '2px 6px',
                                            borderRadius: '4px',
                                            minWidth: '20px',
                                            textAlign: 'center'
                                        }}>
                                            {month.count}
                                        </span>
                                    </div>
                                ))}

                                <div style={{
                                    fontSize: '14px',
                                    color: '#718096',
                                    fontWeight: '500'
                                }}>
                                    Total: {filteredSessions.length}
                                </div>
                            </div>
                        </div>

                        <div style={{
                            padding: '30px',
                            overflowX: 'auto',
                            maxHeight: filteredSessions.length > 10 ? '500px' : 'none',
                            overflowY: filteredSessions.length > 10 ? 'auto' : 'visible'
                        }}>
                            {filteredSessions.length > 0 ? (
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead style={{
                                    //position: filteredSessions.length > 10 ? 'sticky' : 'static',
                                    position: 'sticky',
                                    top: 0,
                                    zIndex: 10,
                                    backgroundColor: '#4a6eb5',
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                }}>
                                    <tr style={{ backgroundColor: '#4a6eb5' }}>
                                        <th style={{
                                            padding: '12px 16px',
                                            textAlign: 'center',
                                            fontSize: '14px',
                                            fontWeight: '600',
                                            color: 'white',
                                            borderRadius: '8px 0 0 0'
                                        }}>
                                            Fecha
                                        </th>
                                        <th style={{
                                            padding: '12px 16px',
                                            textAlign: 'center',
                                            fontSize: '14px',
                                            fontWeight: '600',
                                            color: 'white'
                                        }}>
                                            Hora
                                        </th>
                                        <th style={{
                                            padding: '12px 16px',
                                            textAlign: 'center',
                                            fontSize: '14px',
                                            fontWeight: '600',
                                            color: 'white'
                                        }}>
                                            Servicio
                                        </th>
                                        <th style={{
                                            padding: '12px 16px',
                                            textAlign: 'center',
                                            fontSize: '14px',
                                            fontWeight: '600',
                                            color: 'white'
                                        }}>
                                            Sesiones/Semana
                                        </th>
                                        <th style={{
                                            padding: '12px 16px',
                                            textAlign: 'center',
                                            fontSize: '14px',
                                            fontWeight: '600',
                                            color: 'white',
                                            borderRadius: '0 8px 0 0'
                                        }}>
                                            # Sesión
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredSessions.map((session, index) => (
                                        <tr
                                            key={session.id || index}
                                            style={{
                                                borderBottom: '1px solid #e5e9f2',
                                                transition: 'background-color 0.2s'
                                            }}
                                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fc'}
                                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                        >
                                            <td style={{ padding: '12px 16px', fontSize: '14px', color: '#2d3748' }}>
                                                {moment(session.login).tz(ARGENTINA_TIMEZONE).format('DD/MM/YYYY')}
                                            </td>
                                            <td style={{ padding: '12px 16px', fontSize: '14px', color: '#2d3748' }}>
                                                {moment(session.login).tz(ARGENTINA_TIMEZONE).format('HH:mm')}
                                            </td>
                                            <td style={{ padding: '12px 16px', fontSize: '14px' }}>
                                                {session.service && SERVICE_CONFIG[session.service] ? (
                                                    <span style={{
                                                        padding: '4px 8px',
                                                        borderRadius: '12px',
                                                        fontSize: '12px',
                                                        fontWeight: '500',
                                                        backgroundColor: `${SERVICE_CONFIG[session.service].color}20`,
                                                        color: SERVICE_CONFIG[session.service].color
                                                    }}>
                                                        {SERVICE_CONFIG[session.service].icon} {SERVICE_CONFIG[session.service].name}
                                                    </span>
                                                ) : (
                                                    <span style={{ color: '#9ca3af' }}>-</span>
                                                )}
                                            </td>
                                            <td style={{
                                                padding: '12px 16px',
                                                fontSize: '14px',
                                                color: '#2d3748',
                                                textAlign: 'center'
                                            }}>
                                                {session.sessionsWeek || '-'}
                                            </td>
                                            <td style={{
                                                padding: '12px 16px',
                                                fontSize: '14px',
                                                color: '#2d3748',
                                                textAlign: 'center'
                                            }}>
                                                {session.sessionNumber || '-'}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            ) : (
                                <div style={{ textAlign: 'center', color: '#718096', padding: '20px 0' }}>
                                    No hay asistencias para el periodo seleccionado.
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {!state.selectedUserId && !state.loadingUsers && (
                    <div style={{
                        textAlign: 'center',
                        padding: '60px 20px',
                        color: '#9ca3af'
                    }}>
                        <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔍</div>
                        <h3 style={{
                            fontSize: '20px',
                            fontWeight: '600',
                            color: '#374151',
                            marginBottom: '8px'
                        }}>
                            Selecciona un usuario para ver sus sesiones
                        </h3>
                        <p style={{
                            fontSize: '16px',
                            color: '#6b7280',
                            maxWidth: '400px',
                            margin: '0 auto'
                        }}>
                            Utiliza el buscador para encontrar a un miembro por nombre o DNI
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default MemberAttendanceTracker;
